import { useStoryblokApi, useStoryblokBridge } from "@storyblok/vue";
import { useState, onMounted } from "#imports";
import {
  inlineStoryAuthors,
  type ResolvedAuthorStory,
} from "./AuthorStructuredDataSchema";

const isStoryblokPreview = () =>
  typeof window !== "undefined" &&
  (window.location.search.includes("_storyblok") ||
    window.location.search.includes("_storyblok_tk"));

const hasAuthorRelation = (resolveRelations: unknown) =>
  typeof resolveRelations === "string" && resolveRelations.includes(".author");

const isMissingAuthorField = (story: Record<string, unknown> | null) => {
  const author = (story?.content as { author?: unknown } | undefined)?.author;

  if (author == null) return true;
  if (Array.isArray(author)) return author.length === 0;

  return false;
};

export const useAsyncStoryblokWithRels = async (
  url: string,
  apiOptions: Record<string, unknown> = {},
  bridgeOptions: Record<string, unknown> = {}
) => {
  const uniqueKey = `${JSON.stringify(apiOptions)}${url}`;
  const story = useState<Record<string, unknown> | null>(
    `${uniqueKey}-state`,
    () => null
  );
  const rels = useState<ResolvedAuthorStory[]>(`${uniqueKey}-rels`, () => []);
  const storyblokApiInstance = useStoryblokApi();
  const shouldResolveAuthors = hasAuthorRelation(apiOptions.resolve_relations);

  onMounted(() => {
    if (!story.value?.id || !isStoryblokPreview()) return;

    useStoryblokBridge(
      story.value.id as number,
      (evStory) => {
        story.value = evStory;
        inlineStoryAuthors(
          (story.value?.content ?? null) as { author?: unknown } | null,
          rels.value
        );
      },
      bridgeOptions
    );
  });

  const needsFetch =
    !story.value ||
    (shouldResolveAuthors && !rels.value.length) ||
    (shouldResolveAuthors && isMissingAuthorField(story.value));

  if (needsFetch) {
    const { data } = await storyblokApiInstance.get(`cdn/stories/${url}`, apiOptions);
    story.value = data.story;
    rels.value = data.rels ?? [];
  }

  return { story, rels };
};
